import {
  Badge,
  Box,
  Button,
  Collapse,
  Divider,
  Flex,
  HStack,
  Heading,
  Icon,
  Text,
  VStack,
  useDisclosure,
} from "@chakra-ui/react";
import Amenity from "../../accommodation/Amenity";
import { ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons";
import { TbAutomaticGearbox } from "react-icons/tb";
import { MdAirlineSeatReclineExtra } from "react-icons/md";
import { BsFuelPumpFill } from "react-icons/bs";
import { FaCar } from "react-icons/fa6";
import { capitalize } from "lodash";
import { useNavigate } from "react-router-dom";
import { formatDateShortCET, formatDateLongCET, labelTimeCET } from "../../../utils/helpers";
import { ACCOMMODATION_TYPES } from "../../../constants";
import { useTranslation } from "react-i18next";
import { getTransportationTypeName } from "../../../utils/vehicleDisplay";
import { getDateInServerExpectedFormat } from "../../../utils/helpers";

function BookingSummaryStep({
  bookingType,
  isForeignPartnerReservation,
  abandonDraft,
  ...props
}) {
  const { t } = useTranslation(["common", "vehicle"]);

  const { isOpen: isOpenUnitsCollapsible, onToggle: onUnitsCollapsibleToggle } =
    useDisclosure();

  const navigate = useNavigate();

  function AccommodationBookingSummary({ accommodation }) {
    return (
      <Box
        width="full"
        border="1px"
        borderColor="gray.200"
        borderRadius="lg"
        padding="15px"
      >
        <VStack align="flex-start" spacing="2">
          <Text fontSize="sm">{accommodation.type}</Text>
          <Heading as="h3" size="sm">
            {accommodation.name}
          </Heading>
          <Text fontSize="sm">{`${accommodation.address}, ${accommodation.city}`}</Text>
          <Text>
            <Badge fontSize="small" colorScheme="whatsapp">
              {accommodation.rating}
            </Badge>
          </Text>
          <HStack spacing={2} wrap="wrap">
            {Object.keys(accommodation.amenities).map((amenity, index) => (
              <Amenity key={index} amenityKey={amenity} size={"sm"} />
            ))}
          </HStack>
        </VStack>
      </Box>
    );
  }

  function CarBookingSummary({ car }) {
    return (
      <Box
        width="full"
        border="1px"
        borderColor="gray.200"
        borderRadius="lg"
        padding="15px"
      >
        <VStack align="flex-start" spacing="2">
          <Heading as="h3" size="sm">
            {car.name}
          </Heading>
          <Text>
            {/*<Badge fontSize="small">{car.productionYear}</Badge>*/}
          </Text>
          <Flex gap={5} flexWrap="wrap" fontSize="sm">
            <HStack>
              <Icon as={FaCar}></Icon>
              <Text>{capitalize(car.vehicleType)}</Text>
            </HStack>
            <HStack>
              <Icon as={TbAutomaticGearbox} />
              <Text>{car.automatic ? t("bookingAutomatic") : t("bookingManual")}</Text>
            </HStack>
            <HStack>
              <Icon as={MdAirlineSeatReclineExtra}></Icon>
              <Text>{car.numberOfSeats} {t("bookingSeats")}</Text>
            </HStack>
            <HStack>
              <Icon as={BsFuelPumpFill}></Icon>
              <Text>{capitalize(car.fuelType)}</Text>
            </HStack>
          </Flex>
        </VStack>
      </Box>
    );
  }

  function TourBookingSummary({ tour }) {
    return (
      <Box
        width="full"
        border="1px"
        borderColor="gray.200"
        borderRadius="lg"
        padding="15px"
      >
        <VStack align="flex-start" spacing="2">
          <Text fontSize="sm">{t("bookingTour")}</Text>
          <Heading as="h3" size="sm">
            {tour.name}
          </Heading>
        </VStack>
      </Box>
    );
  }

  const details = props.details;
  let accommodationUnits = [];
  if (bookingType === "stay") accommodationUnits = props.accommodationUnits;

  const buildFallbackReturnUrl = () => {
    const start = getDateInServerExpectedFormat(details.checkIn);
    const end = getDateInServerExpectedFormat(details.checkOut);
    const adults = details.adults ?? 2;
    const children = details.children ?? 0;

    if (bookingType === "car") {
      return `/cars/search?start_date=${start}&end_date=${end}&adults=${adults}&children=${children}&rooms=1`;
    }
    if (bookingType === "tour") {
      return `/tours/builder?start_date=${start}&end_date=${end}&adults=${adults}&children=${children}`; // &rooms=1 — omitted until tour stay picker needs it
    }
    return "/stays/search";
  };

  const handleChangeSelection = async () => {
    if (abandonDraft) {
      await abandonDraft();
    }

    if (bookingType === "car" || bookingType === "tour") {
      if (window.history.length > 1) {
        navigate(-1);
        return;
      }
      navigate(buildFallbackReturnUrl(), {
        state: bookingType === "tour" ? { scrollToTop: true } : undefined,
      });
      return;
    }

    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate(buildFallbackReturnUrl());
  };

  return (
    <VStack gap="5">
      {bookingType === "stay" ? (
        <AccommodationBookingSummary accommodation={props.accommodation} />
      ) : bookingType === "car" ? (
        <CarBookingSummary car={props.car} />
      ) : (
        <TourBookingSummary tour={props.tour} />
      )}
      <Box
        width="full"
        border="1px"
        borderColor="gray.200"
        borderRadius="lg"
        padding="15px"
      >
        <VStack align="flex-start" spacing="2">
          <Heading as="h3" size="sm">
            {t("bookingReservationDetails")}
          </Heading>
          <HStack height="90px">
            <Flex flexDir="column" justifyContent="space-between">
              <Text fontSize="sm">
                {bookingType === "car" ? t("bookingPickUp") : t("bookingCheckIn")}
              </Text>
              <Text flexWrap="nowrap" fontWeight="bold">
                {`${formatDateLongCET(details.checkIn)}, ${
                  details.pickupTime ? labelTimeCET(details.pickupTime) : labelTimeCET("14:00")
                }`}
              </Text>
            </Flex>
            <Divider orientation="vertical" />
            <Flex flexDir="column" justifyContent="space-between">
              <Text fontSize="sm">
                {bookingType === "car" ? t("bookingDropOff") : t("bookingCheckOut")}
              </Text>
              <Text fontWeight="bold">
                {`${formatDateLongCET(details.checkOut)}, ${
                  details.dropoffTime ? labelTimeCET(details.dropoffTime) : labelTimeCET("10:00")
                }`}
              </Text>
            </Flex>
          </HStack>
          <Flex direction="column">
            <Text fontSize="sm">
              {bookingType === "car" ? t("bookingTotalLengthRent") : t("bookingTotalLengthStay")}
            </Text>
            <Text fontWeight="bold">{`${details.totalNights} ${
              bookingType === "stay" ? t("bookingNights") : t("bookingDays")
            }`}</Text>
          </Flex>

          <Divider />

          {bookingType === "stay" && (
            <>
              <Button
                display="flex"
                justifyContent="space-between"
                width="full"
                variant="unstyled"
                onClick={onUnitsCollapsibleToggle}
                rightIcon={
                  isOpenUnitsCollapsible ? (
                    <ChevronUpIcon boxSize="6" />
                  ) : (
                    <ChevronDownIcon boxSize="6" />
                  )
                }
              >
                <Flex direction="column" align="flex-start">
                  <Text fontWeight="light" fontSize="sm">
                    {t("bookingYouSelected")}
                  </Text>
                  <Text fontWeight="bold">
                    {t("bookingRoomsFor", {
                      rooms: accommodationUnits.reduce(
                        (totalRooms, a) => totalRooms + a.quantity,
                        0
                      ),
                      adults: details.adults,
                      children: details.children ? details.children : "0",
                    })}
                  </Text>
                </Flex>
              </Button>
              <Collapse in={isOpenUnitsCollapsible} animateOpacity>
                <Flex direction="column" align="flex-start">
                  {accommodationUnits.map((unit, index) => (
                    <Text key={index} fontWeight="light" fontSize="sm">
                      {`${unit.quantity}x ${unit.name} (${formatDateShortCET(
                        unit.startDate
                      )}  -  ${formatDateShortCET(unit.endDate)})`}
                      {unit?.stay?.type === ACCOMMODATION_TYPES.HOTEL
                        ? ` - ${unit.stay.name}`
                        : ""}
                    </Text>
                  ))}
                </Flex>
              </Collapse>
            </>
          )}

          {bookingType === "car" && (
            <>
              <Flex direction="column">
                <Text fontSize="sm">{t("bookingPickUpLocation")}</Text>
                <Text fontWeight="bold">
                  {
                    //check jel za auto da uvijek Sarajeov ili odabire ??
                    details.pickupLocation || "Sarajevo"
                  }
                </Text>
              </Flex>
              <Flex direction="column">
                <Text fontSize="sm">{t("bookingTotalPassengers")}</Text>
                <Text fontWeight="bold">{`${details.adults} ${t("bookingAdults")}, ${
                  details.children ? details.children : "0"
                } ${t("bookingChildren")}`}</Text>
              </Flex>
            </>
          )}

          {bookingType === "tour" && (
            <>
              <Flex direction="column">
                <Text fontSize="sm">{t("bookingPickUpLocation")}</Text>
                <Text fontWeight="bold">
                  {<Text>{`${props.tour.pickupLocation}`}</Text>}
                </Text>
              </Flex>
              <Flex direction="column">
                <Text fontSize="sm">{t("bookingVehicleCategory")}</Text>
                <Text fontWeight="bold">
                  {t("bookingPassengerSeatsWithDriver", {
                    name: getTransportationTypeName(
                      props.tour.selectedVehicleCategory,
                      t
                    ),
                    seats: props.tour.selectedVehicleCategory.seats,
                  })}
                </Text>
              </Flex>
              <Flex direction="column">
                <Text fontSize="sm">{t("bookingDriverIncluded")}</Text>
                <Text fontWeight="bold">{t("bookingYes")}</Text>
              </Flex>
              <Flex direction="column">
                <Text fontSize="sm">{t("bookingTotalGuests")}</Text>
                <Text fontWeight="bold">{`${details.adults} ${t("bookingAdults")}, ${
                  details.children ? details.children : "0"
                } ${t("bookingChildren")}`}</Text>
              </Flex>
            </>
          )}
          { !isForeignPartnerReservation &&
          <Button
            size="sm"
            variant="ghost"
            colorScheme="blue"
            padding="8px"
            margin="0 0 -8px -8px"
            onClick={handleChangeSelection}
          >
            {t("bookingChangeSelection")}
          </Button>
          }
        </VStack>
      </Box>

      <Box
        border="1px"
        borderColor="gray.200"
        borderRadius="lg"
        padding="15px"
        width="full"
      >
        <VStack align="flex-start" spacing="2">
          <Heading as="h3" size="sm">
            {t("bookingYourPriceSummary")}
          </Heading>
          <VStack width="full">
            {details.priceSummary.entries.map((entry, index) => (
              <Flex
                key={index}
                width="full"
                align="center"
                justifyContent="space-between"
                //wrap={"wrap"}
              >
                <>
                  {/*<Text wordBreak={true} fontSize="2xs">{`${entry.name}  (${formatDate(
                    entry.startDate
                  )} - ${formatDate(entry.endDate)})`}</Text>*/}
                  <Text wordBreak={true} fontSize="sm">{`${entry.name}`}</Text>
                  <Text fontSize="sm">{`${entry.totalPricePerUnit}`}</Text>
                </>
              </Flex>
            ))}
            <Divider/>
            <Flex width="full" align="center" justifyContent="space-between">
              {details.priceSummary.vehicleCategory !== null &&
              details.priceSummary.vehicleCategory !== undefined ? (
                <Flex width="full" direction={"column"} align="end" justifyContent="space-between">
                  <Flex width="full" align="center" justifyContent="space-between">
                  <Text fontSize="2xs">{t("bookingVehicleCategory")}</Text>
                  <Text fontSize="xs">{`${getTransportationTypeName(details.priceSummary.vehicleCategory, t)}: EUR ${details.priceSummary.vehicleCategory.price}/day`}</Text>
                </Flex>
                  <Text fontSize="xs">{t("bookingDriverGuide")}</Text>
                  <Text fontSize="xs">{`${t("bookingTotal")} EUR ${(((details.priceSummary.vehicleCategory.price * details.totalNights) + (35.80 * details.totalNights)).toFixed(2))}`}</Text>
                </Flex>
              ) : null}
            </Flex>
          </VStack>
          <Flex width="full" align="center" justifyContent="space-between">
            <Text fontWeight="bold">{t("bookingTotalPrice")}</Text>
            <Text fontWeight="bold">{`${details.priceSummary.totalPrice}`}</Text>
          </Flex>
        </VStack>
      </Box>
    </VStack>
  );
}

export default BookingSummaryStep;
